import { IBackendCartEntries, ILegacyCartEntry } from '@rbi-ctg/menu';
import { OrderSuccessFailureStatuses } from 'state/order/constants';
import { OrderStatus } from 'state/order/types';

import { CartEntryType } from './cart/types';

interface HydrationStatus {
  processed: boolean;
  executing: boolean;
  error: boolean;
}

interface ShouldRedirectToMenuParams {
  isMounted: boolean;
  hydrationStatus: HydrationStatus;
  cartEntries: any[];
  orderStatus: OrderStatus;
  serviceMode: any;
  orderServiceMode: any;
  hasItemsOrCombos: boolean;
}

/**
 * Determines if the user should be redirected to the menu based on cart state and conditions
 */
export const shouldRedirectToMenu = ({
  isMounted,
  hydrationStatus,
  cartEntries,
  orderStatus,
  serviceMode,
  orderServiceMode,
  hasItemsOrCombos,
}: ShouldRedirectToMenuParams): boolean => {
  // Don't redirect if component is not mounted or hydration is still executing
  if (!isMounted || hydrationStatus.executing) {
    return false;
  }

  const hydrationSuccessful =
    hydrationStatus.processed && !hydrationStatus.executing && !hydrationStatus.error;

  const cartIsEmpty = cartEntries.length === 0;
  const isPriceOrderInProgress = OrderSuccessFailureStatuses.inProgress.pos.includes(
    orderStatus as OrderStatus
  );
  const isServiceModeSet = !!serviceMode || !!orderServiceMode;

  // Redirect to Menu if:
  // - Cart is empty
  // - Selected offer is null
  // - The order has not been inserted or has been requested
  // - Hydration was completed successfully
  // Or
  // - Service mode is null
  return (
    (hydrationSuccessful && cartIsEmpty && !isPriceOrderInProgress) ||
    !hasItemsOrCombos ||
    !isServiceModeSet
  );
};

export const isItemOrCombo = (entry: any) => {
  const isItemOrCombo =
    entry.type === CartEntryType.combo ||
    entry.type === CartEntryType.offerCombo ||
    entry.type === CartEntryType.item;
  return isItemOrCombo && !entry.isDonation && !entry.isExtra;
};

export const cartHasItemsOrCombos = (cartEntries: any[]) => {
  return cartEntries.some(isItemOrCombo);
};

export const isBackendCartEntry = (
  entry: ILegacyCartEntry | IBackendCartEntries
): entry is IBackendCartEntries => !('_id' in entry);

// Transforms the 1M sanityId to a legacy sanityId
// item_281$Modifier_845 will translate to Modifier_845
function extractSanityId(modifier: string): string {
  return modifier.split('$')[1] || '';
}

// This function makes the reorderable cartEntries data compatible between legacy and 1M stores
export function normalizeCartEntries(cartEntries: IBackendCartEntries[]): IBackendCartEntries[] {
  return cartEntries.map(cartEntry => ({
    ...cartEntry,
    sanityId: cartEntry.sanityId?.includes('$')
      ? extractSanityId(cartEntry.sanityId)
      : cartEntry.sanityId ?? '',
    children: cartEntry.children ? normalizeCartEntries(cartEntry.children) : cartEntry.children,
  }));
}
